import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useBetterAuth } from '../../hooks/useAuth';
import { AuthUser } from '@lib/betterAuth';
import { AuthLoadingFallback } from './AuthLoadingFallback';
import { LandingRedirect } from './LandingRedirect';

interface RouteGuardProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  requireRole?: AuthUser['role'];
  requireAdmin?: boolean;
  redirectTo?: string;
  fallback?: React.ReactNode;
}

/** Route guard: enforces auth + role/admin requirements before rendering children. */
export const RouteGuard: React.FC<RouteGuardProps> = ({
  children,
  requireAuth = true,
  requireRole,
  requireAdmin = false,
  redirectTo,
  fallback,
}) => {
  const { isAuthenticated, user, hasRole, loading } = useBetterAuth();
  const location = useLocation();

  if (loading) {
    return fallback || <AuthLoadingFallback variant="full" />;
  }

  if (requireAuth && !isAuthenticated) {
    return <Navigate to={redirectTo || '/login'} state={{ from: location }} replace />;
  }

  // Authenticated user hitting a guest-only page (e.g. /login) → home.
  if (!requireAuth && isAuthenticated) {
    return <LandingRedirect />;
  }

  if (requireAdmin && user?.role !== 'admin') {
    return <Navigate to="/unauthorized" replace />;
  }

  if (requireRole && !hasRole(requireRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

/** Requires authentication. */
export const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <RouteGuard requireAuth={true}>{children}</RouteGuard>;
};

/** Allows only unauthenticated visitors (e.g. login page). */
export const GuestGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <RouteGuard requireAuth={false}>{children}</RouteGuard>;
};

/** Requires admin role. */
export const AdminGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <RouteGuard requireAuth={true} requireAdmin={true}>
      {children}
    </RouteGuard>
  );
};

/** Requires the given role. */
export const RoleGuard: React.FC<{
  children: React.ReactNode;
  role: AuthUser['role'];
}> = ({ children, role }) => {
  return (
    <RouteGuard requireAuth={true} requireRole={role}>
      {children}
    </RouteGuard>
  );
};
